import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Header,
  SpaceBetween,
  Button,
  Box,
  Alert,
  Modal,
  LiveRegion,
  Tiles,
  Hotspot
} from '@cloudscape-design/components';
import LoadingBar from "@cloudscape-design/chat-components/loading-bar";
import SupportPromptGroup from "@cloudscape-design/chat-components/support-prompt-group";
import ChatBubble from "@cloudscape-design/chat-components/chat-bubble";
import Avatar from "@cloudscape-design/chat-components/avatar";
import DocumentViewer from '../../components/DocumentViewer';
import AgentPipelineVisualisation from '../../components/AgentPipelineVisualisation';
import apiService from '../../services/api';
import graphEngineWebSocketService from '../../services/graphEngineWebSocket';
import resultsCache from '../../services/resultsCache';
import { useFlowStep } from '../../hooks/useAnalytics';
import { HOTSPOT_IDS } from '../../context/TutorialContext';

function ProcessPage() {
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(false);
  const [latestProgressEvent, setLatestProgressEvent] = useState(null);
  const [logs, setLogs] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [originalQuestionsData, setOriginalQuestionsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showProcessingModal, setShowProcessingModal] = useState(false);
  const [processingComplete, setProcessingComplete] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [websocket, setWebsocket] = useState(null);
  const [sessionResults, setSessionResults] = useState(null);
  const [pollingInterval, setPollingInterval] = useState(null);

  // Navigate to review page with results
  const navigateToReview = (results) => {
    // Persist to sessionStorage; ReviewPage loads from cache
    if (results) {
      resultsCache.save(results);
    }
    navigate('/v2/review', { state: { sessionId: sessionId } });
  };

  // ROCK-SOLID BACKEND: Polling fallback for WebSocket reliability
  const startPollingFallback = (sessionId) => {
    console.log('🔄 Starting polling fallback for session:', sessionId);

    const pollInterval = setInterval(async () => {
      try {
        const status = await apiService.checkSessionCompletionStatus(sessionId);

        console.log('📊 Polling status:', status);

        if (status.should_proceed_to_review) {
          console.log('✅ All questions completed via polling - proceeding to review');
          clearInterval(pollInterval);
          setPollingInterval(null);

          // Set completion state
          setProcessingComplete(true);
          setIsProcessing(false);

          // Add log entry about polling success
          setLogs(prev => [...prev, {
            timestamp: new Date().toLocaleTimeString(),
            message: `Polling detected completion: ${status.completed_questions}/${status.total_questions} questions completed`,
            level: 'INFO',
            agent: 'Polling Fallback'
          }]);

          // Fetch final results and navigate
          fetchSessionResultsWithRetry(sessionId);
        } else if (status.status === 'processing') {
          // Update progress if we have more info than WebSocket provided
          const progressMessage = `Polling update: ${status.completed_questions}/${status.total_questions} questions (${status.progress_percentage.toFixed(1)}%)`;
          console.log('⏳', progressMessage);

          // Only add log if we haven't seen this progress level before
          setLogs(prev => {
            const lastLog = prev[prev.length - 1];
            if (!lastLog || !lastLog.message.includes(`${status.completed_questions}/${status.total_questions}`)) {
              return [...prev, {
                timestamp: new Date().toLocaleTimeString(),
                message: progressMessage,
                level: 'INFO',
                agent: 'Polling Fallback'
              }];
            }
            return prev;
          });
        }
      } catch (error) {
        console.error('❌ Polling error:', error);
        // Don't spam logs with polling errors, just log to console
      }
    }, 3000); // Poll every 3 seconds

    setPollingInterval(pollInterval);
  };

  // Cleanup polling on component unmount or completion
  const stopPollingFallback = () => {
    if (pollingInterval) {
      console.log('🛑 Stopping polling fallback');
      clearInterval(pollingInterval);
      setPollingInterval(null);
    }
  };

  // Fetch session results with retry logic
  const fetchSessionResultsWithRetry = async (sessionId, maxRetries = 3, delay = 2000) => {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        console.log(`Attempt ${attempt}/${maxRetries}: Fetching results for session ${sessionId}`);
        await fetchSessionResults(sessionId);
        return; // Success, exit retry loop
      } catch (error) {
        console.error(`Attempt ${attempt} failed:`, error);
        if (attempt < maxRetries) {
          console.log(`Retrying in ${delay}ms...`);
          await new Promise(resolve => setTimeout(resolve, delay));
          delay *= 1.5; // Exponential backoff
        } else {
          console.error('All retry attempts failed');
          setLogs(prev => [...prev, {
            timestamp: new Date().toLocaleTimeString(),
            message: `Failed to fetch results after ${maxRetries} attempts`,
            level: 'ERROR',
            agent: 'System'
          }]);
        }
      }
    }
  };

  // Fetch session results from the API
  const fetchSessionResults = async (sessionId) => {
    try {
      console.log(`🔍 Fetching results for session: ${sessionId}`);
      setLogs(prev => [...prev, {
        timestamp: new Date().toLocaleTimeString(),
        message: `Fetching final results for session ${sessionId}...`,
        level: 'INFO',
        agent: 'System'
      }]);

      console.log(`📡 About to call apiService.getAIResearchResults(${sessionId})`);
      const sessionData = await apiService.getAIResearchResults(sessionId);

      console.log('✅ Session results fetched successfully');
      console.log('📊 Session data:', sessionData);
      console.log('🔢 Results count:', sessionData?.results?.length || 0);
      console.log('📋 Session data structure:', {
        hasResults: !!sessionData?.results,
        resultsLength: sessionData?.results?.length,
        sessionId: sessionData?.session_id,
        success: sessionData?.success,
        totalQuestions: sessionData?.total_questions,
        completedQuestions: sessionData?.completed_questions
      });

      if (sessionData && sessionData.results && sessionData.results.length > 0) {
        setSessionResults(sessionData);

        setProcessingComplete(true);  // Set completion state when results are fetched
        setIsProcessing(false);       // Ensure processing state is false
        
        setLogs(prev => [...prev, {
          timestamp: new Date().toLocaleTimeString(),
          message: `Successfully retrieved ${sessionData.results.length} research results`,
          level: 'INFO',
          agent: 'System'
        }]);

        // Store in sessionStorage — clear first to avoid quota errors from previous sessions
        console.log('💾 Storing results in sessionStorage...');
        const serializedData = JSON.stringify(sessionData);
        console.log('📦 Serialized data length:', serializedData.length);
        const saved = resultsCache.save(sessionData);
        if (saved) {
          console.log('✅ Results stored in sessionStorage successfully');
        } else {
          console.warn('⚠️ sessionStorage write failed — results held in memory only, navigation will still work');
        }

        // Navigate to review page with results after a short delay
        setTimeout(() => {
          navigateToReview(sessionData);
        }, 1000);
      } else {
        console.warn('No results found in session data:', sessionData);
        setLogs(prev => [...prev, {
          timestamp: new Date().toLocaleTimeString(),
          message: 'No research results found - session may still be processing',
          level: 'WARNING',
          agent: 'System'
        }]);
      }
    } catch (error) {
      console.error('Error fetching session results:', error);
      console.error('Error details:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data
      });
      setLogs(prev => [...prev, {
        timestamp: new Date().toLocaleTimeString(),
        message: `Error fetching results: ${error.message}`,
        level: 'ERROR',
        agent: 'System'
      }]);
    }
  };

  // Clear analysis state (for Reset Demo functionality)
  const clearAnalysisState = () => {
    // Clean up WebSocket connections
    if (websocket) {
      console.log('🧹 Reset Demo - cleaning up WebSocket...');
      graphEngineWebSocketService.disconnect();
      setWebsocket(null);
    }
    
    // Stop polling
    stopPollingFallback();
    
    // Clear all state
    setLogs([]);
    setProcessingComplete(false);
    setSessionResults(null);
    setIsProcessing(false);
    setSessionId(null);
    setLatestProgressEvent(null);
    
    // Clear session storage
    sessionStorage.removeItem('analysisLogs');
    sessionStorage.removeItem('processingComplete');
    resultsCache.clear();
  };

  // Make clearAnalysisState available globally for Reset Demo
  useEffect(() => {
    window.clearAnalysisState = clearAnalysisState;
    return () => {
      delete window.clearAnalysisState;
    };
  }, []);

  // Ensure arrays are always arrays to prevent map errors - with additional null checks
  const safeQuestions = Array.isArray(questions) ? questions : [];
  const safeLogs = Array.isArray(logs) ? logs : [];
  const safeOriginalQuestionsData = Array.isArray(originalQuestionsData) ? originalQuestionsData : [];

  // Save logs and processing state to sessionStorage when they change
  useEffect(() => {
    if (logs.length > 0) {
      sessionStorage.setItem('analysisLogs', JSON.stringify(logs));
    }
  }, [logs]);

  useEffect(() => {
    sessionStorage.setItem('processingComplete', processingComplete.toString());
  }, [processingComplete]);

  // Debug logging to help identify the map error
  React.useEffect(() => {
    console.log('ProcessPage state debug:', {
      questions: questions,
      questionsType: typeof questions,
      questionsIsArray: Array.isArray(questions),
      logs: logs,
      logsType: typeof logs,
      logsIsArray: Array.isArray(logs),
      originalQuestionsData: originalQuestionsData,
      originalQuestionsDataType: typeof originalQuestionsData,
      originalQuestionsDataIsArray: Array.isArray(originalQuestionsData)
    });
  }, [questions, logs, originalQuestionsData]);

  // WebSocket connection for real-time agent logs using Graph Engine
  const connectWebSocket = async (sessionId) => {
    try {
      // Use environment variable for WebSocket URL, fallback to localhost
      // Remove any trailing /ws from the base URL for Graph Engine
      const envWsUrl = process.env.REACT_APP_WEBSOCKET_URL || 'ws://localhost:8000';
      const baseWsUrl = envWsUrl.replace(/\/ws$/, '');

      console.log('Connecting to Graph Engine WebSocket...');
      console.log('Environment URL:', envWsUrl);
      console.log('Cleaned base URL:', baseWsUrl);
      console.log('Session ID:', sessionId);

      // First, test if Graph Engine is available
      try {
        // Convert WebSocket URL to HTTP URL properly (handle both ws:// and wss://)
        const httpUrl = baseWsUrl.replace('wss://', 'https://').replace('ws://', 'http://');
        const healthResponse = await fetch(`${httpUrl}/api/v2/health`);
        const healthData = await healthResponse.json();
        console.log('Graph Engine health check:', healthData);

        if (healthData.status !== 'healthy') {
          console.warn('Graph Engine is not healthy, WebSocket may not work properly');
        }
      } catch (healthError) {
        console.error('Graph Engine health check failed:', healthError);
        console.warn('Graph Engine may not be available, WebSocket connection may fail');
      }

      // Connect to Graph Engine WebSocket with session ID
      await graphEngineWebSocketService.connect(baseWsUrl, sessionId);

      // Set up progress handler
      const progressHandler = (data) => {
        console.log('Graph Engine WebSocket message:', data);

        // Handle different message types from Graph Engine
        if (data.type === 'progress_update') {
          console.log('Processing progress_update:', data);
          setLatestProgressEvent(data);

          // Use current_question as the message, fallback to progress percentage
          const message = data.current_question ||
            `Progress: ${Math.round(data.progress_percentage || 0)}%`;

          // Show current stage and progress
          const stageInfo = data.current_stage ? ` (${data.current_stage})` : '';
          const progressInfo = data.completed_questions !== undefined && data.total_questions !== undefined ?
            ` [${data.completed_questions}/${data.total_questions}]` : '';

          const logMessage = `${message}${stageInfo}${progressInfo}`;
          console.log('Adding progress log:', logMessage);

          setLogs(prev => {
            const newLogs = [...prev, {
              timestamp: new Date().toLocaleTimeString(),
              message: logMessage,
              level: 'INFO',
              agent: data.current_stage === 'kb_retrieval' ? 'Knowledge Base' : 'Graph Engine'
            }];
            console.log('Updated logs array:', newLogs);
            return newLogs;
          });

          // Check for completion within the progress_update handler
          if (data.progress_percentage >= 100 ||
            (data.completed_questions && data.total_questions &&
              data.completed_questions >= data.total_questions)) {
            console.log('Session completed! Progress:', data.progress_percentage, 'Questions:', data.completed_questions, '/', data.total_questions);
            console.log('Completion data:', data);
            setProcessingComplete(true);
            setIsProcessing(false);

            // Stop polling fallback since WebSocket detected completion
            stopPollingFallback();

            // Fetch results from the session endpoint with retry
            if (sessionId) {
              fetchSessionResultsWithRetry(sessionId);
            }
          }
        } else if (data.type === 'agent_status') {
          setLogs(prev => [...prev, {
            timestamp: new Date().toLocaleTimeString(),
            message: `Agent ${data.agent_name}: ${data.status}`,
            level: 'INFO',
            agent: data.agent_name || 'Agent'
          }]);
        } else if (data.type === 'research_complete') {
          setProcessingComplete(true);
          setIsProcessing(false);

          // Stop polling fallback since WebSocket detected completion
          stopPollingFallback();

          if (data.results) {
            setSessionResults(data.results);
          }
        } else if (data.type === 'progress_update' && data.progress_percentage >= 100) {
          // Session completed - fetch final results with retry logic
          console.log('Session completed (100% progress), fetching results...');
          console.log('Completion data:', data);
          setProcessingComplete(true);
          setIsProcessing(false);

          // Fetch results from the session endpoint with retry
          if (sessionId) {
            fetchSessionResultsWithRetry(sessionId);
          }
        } else if (data.type === 'progress_update' && data.message && data.message.includes('completed')) {
          // Alternative completion detection based on message content
          console.log('Session completed (message-based), fetching results...');
          console.log('Completion data:', data);
          setProcessingComplete(true);
          setIsProcessing(false);

          // Fetch results from the session endpoint with retry
          if (sessionId) {
            fetchSessionResultsWithRetry(sessionId);
          }
        } else if (data.type === 'connection_info') {
          setLogs(prev => [...prev, {
            timestamp: new Date().toLocaleTimeString(),
            message: `WebSocket connected successfully (${data.connection_type})`,
            level: 'INFO',
            agent: 'WebSocket'
          }]);
        } else if (data.type === 'kb_retrieval') {
          // KB retrieval event — show document source being queried
          const docName = data.source || 'Unknown';
          const page = data.page || '?';
          const score = data.score ? `${Math.round(data.score * 100)}%` : '';
          const query = data.query || '';
          const total = data.total_sources || 0;
          setLogs(prev => [...prev, {
            timestamp: new Date().toLocaleTimeString(),
            message: `📄 KB Retrieved: ${docName} (p.${page}) ${score} [${total} sources] — "${query}"`,
            level: 'INFO',
            agent: 'Knowledge Base'
          }]);
        } else if (data.type === 'error') {
          setLogs(prev => [...prev, {
            timestamp: new Date().toLocaleTimeString(),
            message: `Error: ${data.message}`,
            level: 'ERROR',
            agent: 'Graph Engine'
          }]);
        } else {
          // Generic message handling
          setLogs(prev => [...prev, {
            timestamp: new Date().toLocaleTimeString(),
            message: data.message || `Received: ${data.type || 'unknown message'}`,
            level: 'INFO',
            agent: data.agent || 'Graph Engine'
          }]);
        }
      };

      // Add progress handler
      graphEngineWebSocketService.onProgress(progressHandler);

      // Set websocket state to indicate connection
      setWebsocket(graphEngineWebSocketService);

      console.log('Graph Engine WebSocket connected successfully for session:', sessionId);

    } catch (error) {
      console.error('Failed to connect to Graph Engine WebSocket:', error);
      setWebsocket(null);

      // Add error log to UI
      setLogs(prev => [...prev, {
        timestamp: new Date().toLocaleTimeString(),
        message: `WebSocket connection failed: ${error.message}. Continuing without real-time updates.`,
        level: 'WARNING',
        agent: 'System'
      }]);
    }
  };

  // Poll session status
  const pollSessionStatus = async (sessionId) => {
    const maxAttempts = 60; // 5 minutes max
    let attempts = 0;

    const poll = async () => {
      try {
        const status = await apiService.getAIResearchStatus(sessionId);
        console.log('Session status:', status);

        if (status.status === 'completed') {
          console.log('Session completed with results:', status);
          setSessionResults(status);
          setProcessingComplete(true);
          setIsProcessing(false);
          if (websocket) {
            graphEngineWebSocketService.disconnect();
          }
          return;
        } else if (status.status === 'failed') {
          throw new Error(status.error || 'AI research failed');
        }

        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 5000); // Poll every 5 seconds
        } else {
          throw new Error('Session polling timed out');
        }
      } catch (error) {
        console.error('Error polling session status:', error);
        setIsProcessing(false);
        // Fall back to simulation
        fallbackToSimulation();
      }
    };

    poll();
  };

  // Fallback simulation if API fails
  const fallbackToSimulation = () => {
    const agentLogs = [
      'Loading Amazon Annual Report 2024...',
      'Agent 1: Analyzing financial metrics...',
      'Agent 2: Extracting risk factors...',
      'Agent 3: Calculating financial ratios...',
      'Agent 4: Identifying competitive advantages...',
      'Agent 5: Reviewing ESG commitments...',
      'Cross-referencing findings...',
      'Generating citations...',
      'Finalizing analysis...'
    ];

    agentLogs.forEach((log, index) => {
      setTimeout(() => {
        setLogs(prev => [...prev, {
          timestamp: new Date().toLocaleTimeString(),
          message: log,
          level: 'INFO',
          agent: 'Simulation'
        }]);

        if (index === agentLogs.length - 1) {
          setTimeout(() => {
            setProcessingComplete(true);
            setIsProcessing(false);
          }, 1000);
        }
      }, index * 2000);
    });
  };

  // Fetch questions from API on component mount
  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        setLoading(true);
        console.log('Fetching questions from API...');
        const questionsData = await apiService.getQuestionsV2();
        console.log('Questions data received:', questionsData);

        // Check if questionsData is valid
        if (!questionsData || !Array.isArray(questionsData)) {
          console.error('Invalid questions data:', questionsData);
          throw new Error('Invalid questions data received from API');
        }

        if (questionsData.length === 0) {
          console.warn('No questions returned from API');
          throw new Error('No questions available');
        }

        // Store original data for API calls
        setOriginalQuestionsData(questionsData);

        // Transform questions for SupportPromptGroup format with additional safety
        const safeQuestionsData = Array.isArray(questionsData) ? questionsData : [];
        const transformedQuestions = safeQuestionsData.map((q, index) => ({
          id: (q && q.id) || `q_${index + 1}`,
          text: (q && (q.question || q.text)) || 'Question text not available',
          label: `Q${index + 1}`
        }));
        setQuestions(transformedQuestions);
        setError(null);
      } catch (err) {
        console.error('Error loading questions:', err);

        // Fallback to default questions if API fails
        const fallbackQuestions = [
          { id: 'q1', question: "What was Amazon's total revenue in 2024?" },
          { id: 'q2', question: "How does Amazon address competitive threats and maintain its market position across different business segments?" },
          { id: 'q3', question: "What are Amazon's key risk factors mentioned in their 2024 annual report?" },
          { id: 'q4', question: "What is Amazon's strategy for AWS growth and market expansion?" },
          { id: 'q5', question: "How does Amazon approach ESG (Environmental, Social, Governance) commitments?" },
          { id: 'q6', question: "What are Amazon's major capital expenditures and investment priorities?" }
        ];

        console.log('Using fallback questions:', fallbackQuestions);
        setOriginalQuestionsData(fallbackQuestions);

        const safeFallbackQuestions = Array.isArray(fallbackQuestions) ? fallbackQuestions : [];
        const transformedQuestions = safeFallbackQuestions.map((q, index) => ({
          id: (q && q.id) || `q_${index + 1}`,
          text: (q && q.question) || 'Question text not available',
          label: `Q${index + 1}`
        }));
        setQuestions(transformedQuestions);

        setError(`Failed to load questions from API (${err.message}). Using default questions.`);
      } finally {
        setLoading(false);
      }
    };

    // Restore logs and processing state from sessionStorage
    const restoreState = () => {
      try {
        const savedLogs = sessionStorage.getItem('analysisLogs');
        const savedProcessingComplete = sessionStorage.getItem('processingComplete');
        const savedSessionResults = resultsCache.load();
        
        if (savedLogs) {
          const parsedLogs = JSON.parse(savedLogs);
          if (Array.isArray(parsedLogs)) {
            setLogs(parsedLogs);
          }
        }
        
        if (savedProcessingComplete === 'true') {
          setProcessingComplete(true);
        }
        
        if (savedSessionResults) {
          setSessionResults(savedSessionResults);
        }
      } catch (error) {
        console.error('Error restoring state from sessionStorage:', error);
      }
    };

    fetchQuestions();
    restoreState();
  }, []);

  // Cleanup polling and WebSocket on component unmount
  useEffect(() => {
    return () => {
      stopPollingFallback();
      if (websocket) {
        console.log('🧹 Component unmounting - cleaning up WebSocket...');
        graphEngineWebSocketService.disconnect();
      }
    };
  }, [websocket]);

  const startProcessing = async () => {
    // Clean up any existing WebSocket connections first
    if (websocket) {
      console.log('🧹 Cleaning up existing WebSocket connection...');
      graphEngineWebSocketService.disconnect();
      setWebsocket(null);
    }
    
    // Stop any existing polling
    stopPollingFallback();
    
    // Clear session state
    setSessionId(null);
    setSessionResults(null);
    
    setIsProcessing(true);
    setLogs([]);
    setProcessingComplete(false);

    try {
      // Add initial log
      setLogs([{ timestamp: new Date().toLocaleTimeString(), message: 'Initializing Graph Research Engine...', level: 'INFO', agent: 'System' }]);

      // Check if originalQuestionsData is valid
      if (!safeOriginalQuestionsData || safeOriginalQuestionsData.length === 0) {
        throw new Error('No questions data available. Please refresh the page.');
      }

      // Transform questions for V2 batch API
      const batchQuestions = (safeOriginalQuestionsData || []).map((q, index) => ({
        id: `q_${index + 1}`,
        text: (q && (q.question || q.text)) || q || 'Question text not available'
      }));
      console.log('Transformed batch questions:', batchQuestions);

      // Check Graph Engine health first
      try {
        const healthResponse = await apiService.getGraphEngineHealth();
        console.log('Graph Engine health:', healthResponse);

        if (healthResponse.status !== 'healthy') {
          throw new Error(`Graph Engine not healthy: ${healthResponse.status}`);
        }
      } catch (healthError) {
        console.error('Graph Engine health check failed:', healthError);
        throw new Error('Graph Engine is not available');
      }

      // Start AI research using Graph Engine with session ID
      console.log('Starting V2 batch research for Amazon...');
      const response = await apiService.startAIResearch('Amazon', batchQuestions, true);

      console.log('V2 batch research started:', response);

      // Check if the API call was successful
      if (!response.success) {
        throw new Error(`Graph Engine research failed: ${response.error || 'Unknown error'}`);
      }

      if (response.session_id) {
        setSessionId(response.session_id);

        // Connect WebSocket immediately with the session ID from response
        setLogs(prev => [...prev, { timestamp: new Date().toLocaleTimeString(), message: 'Connecting to real-time progress updates...', level: 'INFO', agent: 'System' }]);
        await connectWebSocket(response.session_id);

        // Check if we got results immediately (fast processing)
        if (response.results && response.results.length > 0) {
          setLogs(prev => [...prev, { timestamp: new Date().toLocaleTimeString(), message: '[JS] Research completed successfully!', level: 'INFO', agent: 'Graph Engine' }]);
          setSessionResults(response);
          setProcessingComplete(true);
          setIsProcessing(false);
        } else {
          // If no immediate results, wait for WebSocket updates
          setLogs(prev => [...prev, { timestamp: new Date().toLocaleTimeString(), message: 'Waiting for research results...', level: 'INFO', agent: 'System' }]);

          // ROCK-SOLID BACKEND: Start polling fallback after 30 seconds
          setTimeout(() => {
            /* setLogs(prev => [...prev, {
              timestamp: new Date().toLocaleTimeString(),
              message: 'Starting polling fallback as safety net...',
              level: 'INFO',
              agent: 'System'
            }]); */
            startPollingFallback(response.session_id);
          }, 30000); // 30 seconds delay
        }
      } else {
        throw new Error('No session ID returned from API');
      }

    } catch (error) {
      console.error('Error in AI research:', error);
      setIsProcessing(false);

      // Add error log to UI
      setLogs(prev => [...prev, {
        timestamp: new Date().toLocaleTimeString(),
        message: `WebSocket connection failed: ${error.message}. Continuing without real-time updates.`,
        level: 'WARNING',
        agent: 'System'
      }]);

      // Fallback to simulation if API fails
      fallbackToSimulation();
    }
  };

  const viewResults = () => {
    if (sessionResults) {
      resultsCache.save(sessionResults);
    }
    navigate('/v2/review', { state: { sessionId: sessionId } });
  };

  if (loading) {
    return (
      <Container>
        <SpaceBetween size="l">
          <Header variant="h1">Loading Questions</Header>
          <LiveRegion>
            <Box
              margin={{ bottom: "xs", left: "l" }}
              color="text-body-secondary"
            >
              Loading questions
            </Box>
            <LoadingBar variant="gen-ai" />
          </LiveRegion>
        </SpaceBetween>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <SpaceBetween size="l">
          <Header variant="h1">Error</Header>
          <Alert type="error">{error}</Alert>
        </SpaceBetween>
      </Container>
    );
  }

  return (
    <Container>
      <SpaceBetween size="l">
        <Header
          variant="h1"
          actions={
            !processingComplete ? (
              <Button
                variant="primary"
                iconName="gen-ai"
                onClick={startProcessing}
                disabled={isProcessing}
              >
                Start Analysis
              </Button>
            ) : (
              <Button
                variant="primary"
                iconName="external"
                onClick={() => navigateToReview(sessionResults)}
                disabled={!sessionResults}
              >
                View Results
              </Button>
            )
          }
        >
          <img 
            src="/images/logo/researchAI_Logo.png" 
            alt="ResearchAI" 
            style={{ height: '40px', width: '40px', marginRight: '16px', verticalAlign: 'middle' }}
          />
          AI Processing
        </Header>

        <Alert type={processingComplete ? "success" : "info"}>
          {processingComplete ? "Analysis complete - Review your results" : (
            <>
              Sara is ready for AI analysis with her <strong>4 documents</strong> and <strong>{safeQuestions.length} research questions</strong> for an in depth look at the company profile.
              <Hotspot hotspotId={HOTSPOT_IDS.processReady} direction="bottom" />
            </>
          )}
        </Alert>

        <Container>
          <SpaceBetween size="m">
            <ChatBubble
              ariaLabel="Sara Chen documents introduction"
              type="incoming"
              avatar={
                <Avatar
                  ariaLabel="Sara Chen"
                  imgUrl="/avatars/financial-advisor.jpg"
                  width={40}
                />
              }
            >
              These are the files that I've collected to aid in my research
            </ChatBubble>
            <Tiles
            columns={4}
            items={[
              {
                label: "Amazon Annual Report 2024",
                description: (
                  <>
                    <DocumentViewer
                      documentName="View Amazon Annual Report 2024"
                      documentPath="/Amazon-2024-Annual-Report.pdf"
                      linkVariant="primary"
                    />
                    <Hotspot hotspotId={HOTSPOT_IDS.processDocAnnual} direction="bottom" />
                  </>
                ),
                value: "amazon-2024"
              },
              {
                label: "Amazon Sustainability Report",
                description: (
                  <DocumentViewer
                    documentName="View Amazon Sustainability Report 2024"
                    documentPath="/2024-amazon-sustainability-report.pdf"
                    linkVariant="primary"
                  />
                ),
                value: "amazon-sustainability"
              },
              {
                label: "Amazon Quarterly Report",
                description: (
                  <>
                    <DocumentViewer
                      documentName="View Amazon Q1 2025 Report"
                      documentPath="/Amazon-2025-Quartely-Report.pdf"
                      linkVariant="primary"
                    />
                    <Hotspot hotspotId={HOTSPOT_IDS.processDocQuarterly} direction="bottom" />
                  </>
                ),
                value: "amazon-quarterly"
              },
              {
                label: "Sustainability Executive Summary",
                description: (
                  <DocumentViewer
                    documentName="View Sustainability Summary 2024"
                    documentPath="/2024-sustainability-executive-summary.pdf"
                    linkVariant="primary"
                  />
                ),
                value: "sustainability-summary"
              }
            ]}
            readOnly
          />
          </SpaceBetween>
        </Container>

        <Container>
          <SpaceBetween size="m">
            <ChatBubble
              ariaLabel="Sara Chen research request"
              type="incoming"
              showLoadingBar={isProcessing}
              avatar={
                <Avatar
                  ariaLabel="Sara Chen"
                  imgUrl="/avatars/financial-advisor.jpg"
                  width={40}
                />
              }
            >
              These are my research questions that I'd like detailed answers on.
              <Hotspot hotspotId={HOTSPOT_IDS.processQuestions} direction="bottom" />
            </ChatBubble>
            
            {safeQuestions && safeQuestions.length > 0 ? (
              <SupportPromptGroup
                items={safeQuestions.map(q => ({
                  text: (q && q.text) || 'Question text not available',
                  label: (q && q.label) || 'Q?'
                }))}
                onItemClick={() => { }} // No action needed for display
              />
            ) : (
              <div>Loading questions...</div>
            )}

            <Hotspot hotspotId={HOTSPOT_IDS.processStart} direction="top">
              <Button
                variant="primary"
                iconName="gen-ai"
                onClick={startProcessing}
                disabled={isProcessing}
              >
                Start Analysis
              </Button>
            </Hotspot>
          </SpaceBetween>
        </Container>

        <Container>
          <SpaceBetween size="m">
            <Header 
              variant="h2" 
              description="Information returned by the Strand Agents while processing"
            >
              <img 
                src="/images/logo/researchAI_Logo.png" 
                alt="ResearchAI" 
                style={{ height: '24px', width: '24px', marginRight: '8px', verticalAlign: 'middle' }}
              />
              Analysis Progress
            </Header>

            <Hotspot hotspotId={HOTSPOT_IDS.processPipeline} direction="top">
              <AgentPipelineVisualisation
                isProcessing={isProcessing}
                processingComplete={processingComplete}
                progressData={latestProgressEvent}
              />
            </Hotspot>

            {isProcessing && (
              <LiveRegion>
                <Box
                  margin={{ bottom: "xs", left: "l" }}
                  color="text-body-secondary"
                >
                  Generating AI analysis
                </Box>
                <LoadingBar variant="gen-ai" />
              </LiveRegion>
            )}

            {processingComplete && (
              <Alert type="success">
                Analysis completed successfully! All {safeQuestions.length} questions have been processed.
              </Alert>
            )}

            <div
              style={{
                maxHeight: '400px',
                overflow: 'auto',
                backgroundColor: '#000000',
                color: '#ffffff',
                fontFamily: 'monospace',
                padding: '12px',
                border: '1px solid #414d5c',
                borderRadius: '4px',
                minHeight: '100px'
              }}
            >
              {logs.length === 0 && !isProcessing ? (
                <div style={{ color: '#666', fontStyle: 'italic' }}>
                  Click "Start Analysis" to begin processing...
                </div>
              ) : (
                (safeLogs || []).map((log, index) => (
                  <div key={index} style={{ marginBottom: '4px' }}>
                    <span style={{ color: '#00d4aa' }}>[{log && log.timestamp ? log.timestamp : 'N/A'}]</span>
                    {log && log.agent && <span style={{ color: '#ff9900' }}> [{log.agent}]</span>}
                    {log && log.level && <span style={{ color: '#66d9ef' }}> {log.level}:</span>}
                    <span> {log && log.message ? log.message : 'No message'}</span>
                  </div>
                ))
              )}
              {isProcessing && logs.length > 0 && (
                <div style={{ color: '#00d4aa' }}>▋</div>
              )}
            </div>

            {processingComplete && (
              <Button
                variant="primary"
                iconName="external"
                onClick={() => navigateToReview(sessionResults)}
                disabled={!sessionResults}
              >
                View Results
              </Button>
            )}
          </SpaceBetween>
        </Container>
      </SpaceBetween>

      {/* Processing Modal */}
      <Modal
        visible={showProcessingModal}
        onDismiss={() => {
          if (!isProcessing) {
            setShowProcessingModal(false);
            // Don't reset processingComplete - keep it true if analysis finished
          }
        }}
        header="AI Analysis in Progress"
        size="large"
        footer={
          <Box float="right">
            <Button
              variant="primary"
              onClick={viewResults}
              disabled={!processingComplete}
              loading={isProcessing}
            >
              {isProcessing ? 'Processing...' : 'View Results'}
            </Button>
          </Box>
        }
      >
        <SpaceBetween size="m">
          {isProcessing && (
            <LiveRegion>
              <Box
                margin={{ bottom: "xs", left: "l" }}
                color="text-body-secondary"
              >
                Generating AI analysis
              </Box>
              <LoadingBar variant="gen-ai" />
            </LiveRegion>
          )}

          {processingComplete && (
            <Alert type="success">
              Analysis completed successfully! All {safeQuestions.length} questions have been processed.
            </Alert>
          )}

          <Container>
            <Header variant="h3">Agent Activity Logs</Header>
            <div
              style={{
                maxHeight: '400px',
                overflow: 'auto',
                backgroundColor: '#000000',
                color: '#ffffff',
                fontFamily: 'monospace',
                padding: '12px',
                border: '1px solid #414d5c',
                borderRadius: '4px'
              }}
            >
              {(safeLogs || []).map((log, index) => (
                <div key={index} style={{ marginBottom: '4px' }}>
                  <span style={{ color: '#00d4aa' }}>[{log && log.timestamp ? log.timestamp : 'N/A'}]</span>
                  {log && log.agent && <span style={{ color: '#ff9900' }}> [{log.agent}]</span>}
                  {log && log.level && <span style={{ color: '#66d9ef' }}> {log.level}:</span>}
                  <span> {log && log.message ? log.message : 'No message'}</span>
                </div>
              ))}
              {isProcessing && logs.length > 0 && (
                <div style={{ color: '#00d4aa' }}>▋</div>
              )}
            </div>
          </Container>
        </SpaceBetween>
      </Modal>
    </Container>
  );
}

export default ProcessPage;
